// ts-rest contract of the review controls of a corporate action (SEP-360).
//
//   PUT  /api/coac/actions/:id/supporting-link   Ops sets or removes the supporting link (section 6)
//   GET  /api/coac/actions/:id/sign-offs         the IM sign-offs that the caller may read (section 5)
//   POST /api/coac/actions/:id/sign-off          an IM signs off the responses of their own book
//   GET  /api/coac/titan-emails                  the Titan corporate-action emails (section 2)
//   GET  /api/coac/actions/:id/eligible-holders  the Titan eligible holders, matched (section 3)
//   POST /api/coac/actions/:id/eligible-holders/add-missing  Ops adds the matched portfolios
//
// Outside ts-rest (multipart or bytes): POST /api/coac/titan-emails imports a
// saved Outlook email, GET /api/coac/titan-emails/:id/original and
// GET /api/coac/titan-emails/:id/attachments/:index give the files.
//
// Each route is behind its own catalogue feature, so a deployment that holds
// the feature gets 404 from the route.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';
import { coacSupportingLinkSchema } from './coac';

const c = initContract();

/** The body of a supporting-link change. A null url removes the link. */
export const coacSetSupportingLinkRequestSchema = z.object({
    url: z.string().max(2000).nullable(),
    label: z.string().max(200).nullable(),
});
export type CoacSetSupportingLinkRequest = z.infer<typeof coacSetSupportingLinkRequestSchema>;

/**
 * The state of the sign-off of one IM (SEP-360 section 5).
 *
 * - `not_signed_off`: the IM did not sign off yet.
 * - `signed_off`: the IM signed off, and no response of the book changed since.
 * - `changed_after_sign_off`: a response of the book changed after the
 *   sign-off, so the IM must sign off again.
 */
export const coacImSignOffStatusSchema = z.enum([
    'not_signed_off',
    'signed_off',
    'changed_after_sign_off',
]);
export type CoacImSignOffStatus = z.infer<typeof coacImSignOffStatusSchema>;

/** The sign-off of one IM on one corporate action, with the live facts of the book. */
export const coacImSignOffSchema = z.object({
    imUserId: z.string(),
    imName: z.string().nullable(),
    status: coacImSignOffStatusSchema,
    signedAt: z.string().nullable(),
    signedByName: z.string().nullable(),
    /** The portfolios of the book on the corporate action now. */
    portfolioCount: z.number().int(),
    /** The portfolios of the book that have no response now. */
    openPortfolioCount: z.number().int(),
});
export type CoacImSignOff = z.infer<typeof coacImSignOffSchema>;

export const coacSignOffRequestSchema = z.object({
    /** The IM ticked the confirmation that they reviewed the responses. */
    confirmed: z.literal(true),
});
export type CoacSignOffRequest = z.infer<typeof coacSignOffRequestSchema>;

/** One field of a Titan email that Ops must check. */
export const titanEmailProblemSchema = z.object({
    field: z.string(),
    kind: z.enum(['missing', 'ambiguous', 'unreadable']),
    detail: z.string(),
});
export type TitanEmailProblem = z.infer<typeof titanEmailProblemSchema>;

/** One Titan corporate-action email, with its parse and its draft (SEP-360 section 2). */
export const titanEmailSchema = z.object({
    id: z.string(),
    source: z.enum(['mailbox', 'manual']),
    fromAddress: z.string().nullable(),
    subject: z.string(),
    receivedAt: z.string().nullable(),
    importedAt: z.string(),
    importedByName: z.string().nullable(),
    recognised: z.boolean(),
    noticeKind: z.enum(['new', 'update', 'cancellation']),
    securityName: z.string().nullable(),
    isin: z.string().nullable(),
    sedol: z.string().nullable(),
    eventType: z.string().nullable(),
    electionDeadline: z.string().nullable(),
    problems: z.array(titanEmailProblemSchema),
    draftId: z.string().nullable(),
    /** The review state of the draft, or null when the email made no draft. */
    draftStatus: z.string().nullable(),
    /** The live corporate action of the email: the one it updates, or the one its draft made. */
    coacActionId: z.string().nullable(),
    originalFileName: z.string().nullable(),
    attachments: z.array(
        z.object({ index: z.number().int(), name: z.string(), contentType: z.string() }),
    ),
});
export type TitanEmail = z.infer<typeof titanEmailSchema>;

/** The answer of an import of a saved email. */
export const titanEmailImportResultSchema = z.object({
    emailId: z.string(),
    draftId: z.string().nullable(),
    duplicate: z.boolean(),
    recognised: z.boolean(),
    problems: z.array(titanEmailProblemSchema),
});
export type TitanEmailImportResult = z.infer<typeof titanEmailImportResultSchema>;

/** One row of the eligible-holder list of Titan, matched to the records of the firm. */
export const titanEligibleHolderMatchSchema = z.object({
    rowNumber: z.number().int(),
    client: z.string().nullable(),
    portfolio: z.string().nullable(),
    accountReference: z.string().nullable(),
    businessUnit: z.string().nullable(),
    investmentManager: z.string().nullable(),
    /** The quantity that Titan states. */
    quantity: z.number().nullable(),
    status: z.enum(['matched', 'unmatched']),
    /** The portfolio of the firm that the row matched, or null. */
    portfolioRef: z.string().nullable(),
    septimaClientName: z.string().nullable(),
    septimaImName: z.string().nullable(),
    /** The staff user that the Investment Manager column names, or null. */
    titanImUserId: z.string().nullable(),
    /** True when the portfolio is in the affected set of the corporate action. */
    inAffectedSet: z.boolean(),
    /** The quantity of the affected set, or null. */
    septimaQuantity: z.number().nullable(),
    /** Each problem and each discrepancy, in plain words. */
    flags: z.array(z.string()),
});
export type TitanEligibleHolderMatch = z.infer<typeof titanEligibleHolderMatchSchema>;

export const titanEligibleHoldersViewSchema = z.object({
    list: z
        .object({
            id: z.string(),
            fileName: z.string(),
            source: z.enum(['email_attachment', 'manual']),
            importedAt: z.string(),
            importedByName: z.string().nullable(),
            missingHeaders: z.array(z.string()),
            fileProblems: z.array(z.string()),
        })
        .nullable(),
    rows: z.array(titanEligibleHolderMatchSchema),
    /** The affected portfolios of the firm that the Titan list does not name. */
    notInTitanList: z.array(
        z.object({
            portfolioRef: z.string(),
            clientName: z.string(),
            imName: z.string().nullable(),
            quantity: z.number().nullable(),
        }),
    ),
});
export type TitanEligibleHoldersView = z.infer<typeof titanEligibleHoldersViewSchema>;

export const coacControlsContract = c.router(
    {
        eligibleHolders: {
            method: 'GET',
            path: '/coac/actions/:id/eligible-holders',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: titanEligibleHoldersViewSchema,
                404: errorBody,
            },
            summary:
                'Ops: the current Titan eligible-holder list of a corporate action, matched to the firm',
        },
        addMissingEligibleHolders: {
            method: 'POST',
            path: '/coac/actions/:id/eligible-holders/add-missing',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: {
                200: z.object({ added: z.number().int() }),
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary:
                'Ops: add each matched portfolio of the Titan list that the affected set does not hold',
        },
        listTitanEmails: {
            method: 'GET',
            path: '/coac/titan-emails',
            query: z.object({ actionId: z.string().optional() }),
            responses: {
                200: z.object({ emails: z.array(titanEmailSchema) }),
            },
            summary:
                'Ops: the Titan corporate-action emails, newest first, or the emails of one corporate action',
        },
        listSignOffs: {
            method: 'GET',
            path: '/coac/actions/:id/sign-offs',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: z.object({ signOffs: z.array(coacImSignOffSchema) }),
                404: errorBody,
            },
            summary:
                'The IM sign-offs of a corporate action: each IM of the action for Ops, the own book for the Desk',
        },
        signOff: {
            method: 'POST',
            path: '/coac/actions/:id/sign-off',
            pathParams: z.object({ id: z.string() }),
            body: coacSignOffRequestSchema,
            responses: {
                200: coacImSignOffSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'An IM signs off the responses of their own book to a corporate action',
        },
        setSupportingLink: {
            method: 'PUT',
            path: '/coac/actions/:id/supporting-link',
            pathParams: z.object({ id: z.string() }),
            body: coacSetSupportingLinkRequestSchema,
            responses: {
                200: z.object({ supportingLink: coacSupportingLinkSchema.nullable() }),
                400: errorBody,
                404: errorBody,
            },
            summary: 'Ops adds, changes or removes the supporting link of a corporate action',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
